import React, { useMemo } from 'react';
import { Button, FormSelect, Modal, FormNumberInput } from '@/components/UI';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import { trpc } from '@/api/trpc';
import { useQuery } from '@tanstack/react-query';
import { showToast } from '@/components/Layout/Toast';
import {
    buildBucketsForDistribution,
    buildBucketsFromBoundaries,
    buildParameterDistributionQueryInput,
    clampBucketCount,
    extractNumericStats,
    hasNonEmptyString,
    isFiniteNumber
} from './helpers';
import type {
    BucketAlgorithm,
    BucketMode,
    BucketParameterIdentity,
    CanonicalParameterFeatureIdentity,
    ParameterBucketBoundary,
    ParameterDiscoveryMetric,
    ParameterDistributionQueryInput,
    ParameterGroup,
    ParameterTableRow
} from './types';
import type { ParameterDiscoveryObjectiveMode } from '@einhaus/project314-types';

interface CustomBucketsModalProps {
    bucketAlgorithm: BucketAlgorithm;
    bucketCount: number;
    bucketMode: BucketMode;
    customBucketsByIdentityKey: Record<string, ParameterGroup[]>;
    customRanges: ParameterBucketBoundary[];
    executionStatus: 'draft' | 'queued' | 'running' | 'completed' | 'failed' | 'cancelled' | 'deleting';
    experimentId: number;
    getParameterDisplayLabel: (parameter: {
        path: string;
        canonicalFeature?: CanonicalParameterFeatureIdentity | null | undefined;
    }) => string;
    hasInitialized: boolean;
    identity: BucketParameterIdentity | null;
    isOpen: boolean;
    onClose: () => void;
    optimizationMode: ParameterDiscoveryMetric;
    objectiveMode: ParameterDiscoveryObjectiveMode;
    parameter: ParameterTableRow | null;
    realizedOutcomeSetId: number | null;
    selectedDuration: string;
    selectedHorizon: number;
    setBucketAlgorithm: React.Dispatch<React.SetStateAction<BucketAlgorithm>>;
    setBucketCount: React.Dispatch<React.SetStateAction<number>>;
    setBucketMode: React.Dispatch<React.SetStateAction<BucketMode>>;
    setCustomBucketsByIdentityKey: React.Dispatch<React.SetStateAction<Record<string, ParameterGroup[]>>>;
    setCustomRanges: React.Dispatch<React.SetStateAction<ParameterBucketBoundary[]>>;
}

const CustomBucketsModal = ({
    bucketAlgorithm,
    bucketCount,
    bucketMode,
    customBucketsByIdentityKey,
    customRanges,
    executionStatus,
    experimentId,
    getParameterDisplayLabel,
    hasInitialized,
    identity,
    isOpen,
    onClose,
    optimizationMode,
    objectiveMode,
    parameter,
    realizedOutcomeSetId,
    selectedDuration,
    selectedHorizon,
    setBucketAlgorithm,
    setBucketCount,
    setBucketMode,
    setCustomBucketsByIdentityKey,
    setCustomRanges
}: CustomBucketsModalProps) => {
    const realizedOutcomeSetMissing = objectiveMode === 'strategy_exit' && realizedOutcomeSetId === null;
    const parameterDistributionInput = useMemo<ParameterDistributionQueryInput>(() => {
        return buildParameterDistributionQueryInput({
            experimentId,
            duration: selectedDuration,
            horizonSeconds: selectedHorizon,
            identity,
            metric: optimizationMode === 'options' ? 'options' : 'stock',
            objectiveMode,
            realizedOutcomeSetId
        });
    }, [identity, experimentId, objectiveMode, optimizationMode, realizedOutcomeSetId, selectedDuration, selectedHorizon]);

    // On-demand parameter distribution for custom bucketing modal
    const parameterDistributionQuery = useQuery({
        ...trpc.experiment.getParameterDistribution.queryOptions(parameterDistributionInput),
        enabled:
            isOpen &&
            !!identity &&
            hasNonEmptyString(identity.contextFeatureRef) &&
            executionStatus === 'completed' &&
            !!selectedDuration &&
            !!selectedHorizon &&
            !realizedOutcomeSetMissing &&
            hasInitialized,
        // Always refetch when the modal opens so we don't get stuck on cached null results
        // after backend changes or partial job completion.
        staleTime: 0,
        refetchOnMount: 'always',
        refetchOnWindowFocus: false
    });

    const distribution = parameterDistributionQuery.data;

    const bucketBuildResult = useMemo(() => {
        if (!isOpen || !distribution) return null;

        if (bucketMode === 'custom') {
            const ranges =
                customRanges
                    .filter(
                        (r) =>
                            isFiniteNumber(r.min) &&
                            isFiniteNumber(r.max) &&
                            // Require min < max to avoid degenerate ranges
                            r.max > r.min
                    )
                    .map((r) => ({ min: r.min, max: r.max })) ?? [];

            if (!ranges.length) {
                return {
                    buckets: [],
                    totalCount: 0,
                    excludedCount: distribution.values.reduce((sum, v) => sum + v.count, 0)
                };
            }

            return buildBucketsFromBoundaries(distribution, ranges);
        }

        return buildBucketsForDistribution(distribution, bucketAlgorithm, bucketCount);
    }, [isOpen, distribution, bucketMode, customRanges, bucketAlgorithm, bucketCount]);

    const handleBucketModeChange = (mode: BucketMode) => {
        setBucketMode(mode);
    };

    const handleAddCustomRange = () => {
        if (!distribution) return;

        const { numeric } = extractNumericStats(distribution);
        const globalMin = numeric[0]?.value ?? 0;
        const globalMax = numeric[numeric.length - 1]?.value ?? globalMin + 1;

        setCustomRanges((prev) => [
            ...prev,
            {
                min: globalMin,
                max: globalMax
            }
        ]);
    };

    const handleUpdateCustomRange = (index: number, key: keyof ParameterBucketBoundary, value: number) => {
        setCustomRanges((prev) => prev.map((range, i) => (i === index ? { ...range, [key]: value } : range)));
    };

    const handleRemoveCustomRange = (index: number) => {
        setCustomRanges((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSeedCustomFromAutomatic = () => {
        if (!distribution) return;

        const autoBuckets = buildBucketsForDistribution(distribution, bucketAlgorithm, bucketCount);

        if (!autoBuckets.buckets.length) {
            showToast('No automatic buckets available to seed from', 'warning');
            return;
        }

        setCustomRanges(autoBuckets.buckets.map((b) => ({ ...b.boundary })));
        setBucketMode('custom');
    };

    const handleApplyBucketsToView = () => {
        if (!identity || !bucketBuildResult?.buckets.length) return;

        const groups: ParameterGroup[] = bucketBuildResult.buckets.map((bucket) => {
            const { min, max } = bucket.boundary;
            const label = `${min.toFixed(2)}-${max.toFixed(2)}`;
            const { avgProfit } = bucket;
            const stdDev = bucket.stdDev ?? 0;
            const maxProfit = avgProfit + stdDev;
            const minProfit = avgProfit - stdDev;

            return {
                value: label,
                valueLabel: label,
                count: bucket.count,
                avgProfit,
                winRate: bucket.winRate,
                maxProfit,
                minProfit,
                median: avgProfit,
                stdDev: bucket.stdDev ?? undefined,
                coverage: bucket.coverage,
                lift: bucket.lift,
                ciLower: bucket.ciLower ?? undefined,
                ciUpper: bucket.ciUpper ?? undefined,
                hasData: true
            };
        });

        setCustomBucketsByIdentityKey((prev) => ({
            ...prev,
            [identity.bucketKey]: groups
        }));

        onClose();
    };

    const handleResetBucketsForIdentityKey = (bucketKey: string) => {
        setCustomBucketsByIdentityKey((prev) => {
            if (!prev[bucketKey]) return prev;
            const { [bucketKey]: _removed, ...rest } = prev;
            return rest;
        });
    };

    return (
        <Modal
            isOpen={isOpen}
            title={
                identity
                    ? `Custom Buckets • ${getParameterDisplayLabel(
                          parameter ?? {
                              path: identity.parameterPath,
                              canonicalFeature: identity.canonicalFeature
                          }
                      )}`
                    : 'Custom Buckets'
            }
            onClose={onClose}
        >
            {!identity ? (
                <div className="text-zinc-400 text-sm">No parameter selected.</div>
            ) : parameterDistributionQuery.isLoading ? (
                <div className="py-6">
                    <DataLoadingSpinner text="Loading parameter distribution..." />
                </div>
            ) : parameterDistributionQuery.error ? (
                <div className="text-red-400 text-sm">
                    Failed to load distribution: {parameterDistributionQuery.error.message || 'Unknown error'}
                </div>
            ) : !parameterDistributionQuery.data ||
              !bucketBuildResult ||
              (bucketMode === 'auto' && bucketBuildResult.buckets.length === 0) ? (
                <div className="space-y-2 text-sm">
                    <p className="text-zinc-300">No numeric distribution available for this parameter.</p>
                    <p className="text-zinc-500">
                        This can happen when the parameter only has categorical values or insufficient data for the selected
                        duration/horizon.
                    </p>
                </div>
            ) : (
                <div className="space-y-4">
                    <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
                        <div className="space-y-2">
                            <p className="text-sm text-zinc-300">
                                {bucketBuildResult.totalCount.toLocaleString()} samples used •{' '}
                                {bucketBuildResult.excludedCount > 0
                                    ? `${bucketBuildResult.excludedCount.toLocaleString()} non-numeric samples excluded`
                                    : 'All samples numeric'}
                            </p>
                            <p className="text-xs text-zinc-500">
                                Baseline avg profit: {parameterDistributionQuery.data.baseline.toFixed(2)}%
                            </p>
                        </div>
                        <div className="flex flex-col gap-2 md:flex-row md:items-end">
                            <div className="flex rounded bg-zinc-800 p-1 text-xs">
                                <button
                                    className={`px-3 py-1 rounded ${
                                        bucketMode === 'auto' ? 'bg-zinc-700 text-white' : 'text-zinc-400 hover:text-zinc-200'
                                    }`}
                                    type="button"
                                    onClick={() => handleBucketModeChange('auto')}
                                >
                                    Automatic
                                </button>
                                <button
                                    className={`px-3 py-1 rounded ${
                                        bucketMode === 'custom' ? 'bg-zinc-700 text-white' : 'text-zinc-400 hover:text-zinc-200'
                                    }`}
                                    type="button"
                                    onClick={() => handleBucketModeChange('custom')}
                                >
                                    Custom
                                </button>
                            </div>
                            <FormSelect
                                className="md:w-40"
                                disabled={bucketMode === 'custom'}
                                label="Algorithm"
                                options={[
                                    { value: 'equalWidth', label: 'Equal Width' },
                                    { value: 'equalCount', label: 'Equal Count' },
                                    { value: 'logWidth', label: 'Log Width (positive only)' }
                                ]}
                                value={bucketAlgorithm}
                                onChange={(e) => setBucketAlgorithm(e.target.value as BucketAlgorithm)}
                            />
                            <FormNumberInput
                                integer
                                className="md:w-28"
                                disabled={bucketMode === 'custom'}
                                label="Buckets"
                                max={30}
                                min={2}
                                value={bucketCount}
                                onValueChange={(value) => {
                                    // The bucket count always has a value, so an empty field keeps the current one.
                                    if (value === null) return;

                                    setBucketCount(clampBucketCount(value));
                                }}
                            />
                        </div>
                    </div>

                    {bucketMode === 'custom' && (
                        <div className="space-y-3 mb-3">
                            {customRanges.length === 0 ? (
                                <p className="text-xs text-zinc-500">
                                    No custom ranges defined yet. Add a range manually or seed from the automatic buckets.
                                </p>
                            ) : (
                                <div className="space-y-2">
                                    {customRanges.map((range, idx) => (
                                        <div className="flex items-center gap-2" key={`${range.min}-${range.max}-${idx}`}>
                                            <span className="text-xs text-zinc-400 w-20">Range {idx + 1}</span>
                                            <div className="w-32">
                                                <FormNumberInput
                                                    noMargin
                                                    value={Number.isFinite(range.min) ? range.min : null}
                                                    onValueChange={(value) => handleUpdateCustomRange(idx, 'min', value ?? range.min)}
                                                />
                                            </div>
                                            <span className="text-xs text-zinc-500">to</span>
                                            <div className="w-32">
                                                <FormNumberInput
                                                    noMargin
                                                    value={Number.isFinite(range.max) ? range.max : null}
                                                    onValueChange={(value) => handleUpdateCustomRange(idx, 'max', value ?? range.max)}
                                                />
                                            </div>
                                            <button
                                                className="ml-2 text-xs text-red-400 hover:text-red-300"
                                                type="button"
                                                onClick={() => handleRemoveCustomRange(idx)}
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    ))}
                                    <p className="text-[11px] text-zinc-500">
                                        Ranges are inclusive (min ≤ value ≤ max). Overlapping or gapped ranges are allowed, but may affect
                                        interpretation of coverage.
                                    </p>
                                </div>
                            )}
                            <div className="flex items-center justify-between gap-3">
                                <div className="text-xs text-zinc-500">
                                    Use automatic buckets as a starting point, then refine with custom ranges.
                                </div>
                                <div className="flex gap-2">
                                    <Button size="sm" type="button" variant="secondary" onClick={handleAddCustomRange}>
                                        Add range
                                    </Button>
                                    <Button
                                        disabled={!distribution}
                                        size="sm"
                                        type="button"
                                        variant="secondary"
                                        onClick={handleSeedCustomFromAutomatic}
                                    >
                                        Seed from automatic
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="max-h-80 overflow-auto rounded border border-zinc-700/60">
                        <table className="min-w-full text-sm">
                            <thead className="bg-zinc-900/80">
                                <tr>
                                    <th className="px-3 py-2 text-left font-semibold text-zinc-300">Range</th>
                                    <th className="px-3 py-2 text-right font-semibold text-zinc-300">Count</th>
                                    <th className="px-3 py-2 text-right font-semibold text-zinc-300">Avg Profit</th>
                                    <th className="px-3 py-2 text-right font-semibold text-zinc-300">Win%</th>
                                    <th className="px-3 py-2 text-right font-semibold text-zinc-300">Coverage</th>
                                    <th className="px-3 py-2 text-right font-semibold text-zinc-300">Lift</th>
                                    <th className="px-3 py-2 text-right font-semibold text-zinc-300">CI [95%]</th>
                                </tr>
                            </thead>
                            <tbody>
                                {bucketBuildResult.buckets.map((bucket) => {
                                    const { min, max } = bucket.boundary;

                                    const isSignificant =
                                        bucket.ciLower != null && bucket.ciUpper != null && (bucket.ciLower > 0 || bucket.ciUpper < 0);

                                    return (
                                        <tr className="border-t border-zinc-800/60" key={`${min}-${max}`}>
                                            <td className="px-3 py-2 text-zinc-200">
                                                {min.toFixed(2)} – {max.toFixed(2)}
                                            </td>
                                            <td className="px-3 py-2 text-right text-zinc-200">{bucket.count.toLocaleString()}</td>
                                            <td className="px-3 py-2 text-right">
                                                <span
                                                    className={
                                                        bucket.avgProfit >= 0
                                                            ? 'text-green-400 font-semibold'
                                                            : 'text-red-400 font-semibold'
                                                    }
                                                >
                                                    {bucket.avgProfit.toFixed(2)}%
                                                </span>
                                            </td>
                                            <td className="px-3 py-2 text-right text-zinc-200">{bucket.winRate.toFixed(1)}%</td>
                                            <td className="px-3 py-2 text-right text-zinc-200">{(bucket.coverage * 100).toFixed(1)}%</td>
                                            <td className="px-3 py-2 text-right">
                                                <span
                                                    className={
                                                        bucket.lift >= 0 ? 'text-green-400 font-semibold' : 'text-red-400 font-semibold'
                                                    }
                                                >
                                                    {bucket.lift >= 0 ? '+' : ''}
                                                    {bucket.lift.toFixed(2)}%
                                                </span>
                                            </td>
                                            <td className="px-3 py-2 text-right">
                                                {bucket.ciLower == null || bucket.ciUpper == null ? (
                                                    <span className="text-zinc-500">—</span>
                                                ) : (
                                                    <span className={isSignificant ? 'text-green-400 font-semibold' : 'text-zinc-400'}>
                                                        [{bucket.ciLower.toFixed(1)}, {bucket.ciUpper.toFixed(1)}]%
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className="flex items-center justify-between gap-3 pt-2">
                        <div className="text-xs text-zinc-500">
                            These buckets are computed on demand from the underlying value distribution and do not affect impact scores.
                        </div>
                        <div className="flex gap-2">
                            {customBucketsByIdentityKey[identity.bucketKey] && (
                                <Button size="sm" variant="secondary" onClick={() => handleResetBucketsForIdentityKey(identity.bucketKey)}>
                                    Reset to default
                                </Button>
                            )}
                            <Button size="sm" variant="secondary" onClick={onClose}>
                                Close
                            </Button>
                            <Button size="sm" variant="primary" onClick={handleApplyBucketsToView}>
                                Apply to view
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default CustomBucketsModal;
